import type { ReactNode } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';

interface ResultTableProps {
  title: string;
  content: string;
  showComments: boolean;
  onHostClick: (host: string) => void;
}

// Regex для IP и доменов (из legacy erp.js строка 7745)
const HOST_REGEX =
  /([a-zA-Z0-9][-a-zA-Z0-9]*\.)+[a-zA-Z]{2,}|\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}/g;

function makeLinksClickable(
  text: string,
  onHostClick: (host: string) => void
): ReactNode {
  const parts: ReactNode[] = [];
  let lastIndex = 0;
  const regex = new RegExp(HOST_REGEX.source, 'g');
  let match: RegExpExecArray | null;

  while ((match = regex.exec(text)) !== null) {
    // Текст до ссылки
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    // Кликабельная ссылка
    const host = match[0];
    parts.push(
      <button
        key={`${match.index}-${host}`}
        onClick={() => onHostClick(host)}
        className="text-primary hover:underline"
      >
        {host}
      </button>
    );
    lastIndex = match.index + match[0].length;
  }

  // Остаток текста
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts.length > 0 ? parts : text;
}

export function ResultTable({
  title,
  content,
  showComments,
  onHostClick,
}: ResultTableProps) {
  if (!content || content.trim() === '') return null;

  const lines = content.split('\n').filter((line) => {
    // Фильтруем комментарии если не включен showComments
    if (line.startsWith(';') && !showComments) return false;
    return line.trim() !== '';
  });

  if (lines.length === 0) return null;

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <tbody>
              {lines.map((line, idx) => {
                // Проверяем есть ли разделитель key: value
                const colonIndex = line.indexOf(':');
                if (colonIndex > 0 && colonIndex < 40) {
                  const key = line.slice(0, colonIndex).trim();
                  const value = line.slice(colonIndex + 1).trim();
                  return (
                    <tr
                      key={idx}
                      className="border-b border-border last:border-0"
                    >
                      <td className="py-1.5 pr-3 font-medium text-muted-foreground whitespace-nowrap align-top w-0">
                        {key}
                      </td>
                      <td className="py-1.5 break-all w-full">
                        {makeLinksClickable(value, onHostClick)}
                      </td>
                    </tr>
                  );
                }
                return (
                  <tr key={idx} className="border-b border-border last:border-0">
                    <td colSpan={2} className="py-1.5 break-all">
                      {makeLinksClickable(line.trim(), onHostClick)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}
